/**
 * №53 (53.2) — the Workforce board's pure decisions.
 *
 * Split out of `WorkforceBoardPage.tsx` for the reason the console conventions
 * give (`DecisionCard.tsx` beside `decision-card-model.ts`), and for one more:
 * **the interesting cases are not on the board.** The status vocabulary's whole
 * content is precedence — which label wins when a hire is both working and
 * holding a card — and the approved drawing contains no such hire, so a test
 * driving the rendered board cannot reach it. Red-proving the DOM gate caught two
 * defects and waved that one straight through. A pure module can be asserted
 * directly, without bending the fixture away from the drawing to reach an edge.
 */
import type { AgentStatus } from "../../../lib/types";

export type PillTone = "ok" | "you" | "blk" | "wait";
export interface BoardPill { label: string; tone: PillTone }

/**
 * The board's four words, from the engine's three states plus two facts.
 *
 * The engine returns `working | awaiting | available`; the board says
 * `working · needs you · blocked · waiting`. Three map straight through. The
 * ordering below is the ruling:
 *
 * - **An open card outranks everything.** A hire that is working *and* waiting on
 *   your judgement is reported as the second, because that is the one you can act
 *   on. The board exists to surface exactly this.
 * - **Blocked outranks working**, for the same reason one step down.
 * - **`available` gets NO PILL.** There is no crew word for it, and inventing
 *   "idle" for a staff room is the wrong instinct — a person reads it as an
 *   accusation. The "Now:" line says what they last finished instead, which is
 *   the useful half of the same fact.
 */
export function boardPill(st: AgentStatus | undefined): BoardPill | null {
  if (!st) return null;
  if ((st.open_cards ?? 0) > 0) return { label: "needs you", tone: "you" };
  if (st.blocked) return { label: "blocked", tone: "blk" };
  if (st.status === "working") return { label: "working", tone: "ok" };
  if (st.status === "awaiting") return { label: "waiting", tone: "wait" };
  return null;
}

/** "09:41" from an ISO or SQL stamp; null when there is nothing to show. */
export function clockOf(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso.includes("T") ? iso : `${iso.replace(" ", "T")}Z`);
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit", hour12: false });
}
